import type { ReportColumnsSpec, ReportMetricRow } from "@core/components/ReportTreeTable";
import type {
  FinanceStatisticsAggregate,
  FinanceStatisticsChurchEntry,
  NationFinanceStatisticsDistrictEntry,
  StatLeaf,
} from "@core/reports/api/fetchFinanceReport";
import type { PivotModel, PivotMeasureRow } from "@core/reports/adapters/financePivotAdapters";

/**
 * Tab 3 (statistics) adapters. The two single-value sections — nation_office_finances
 * and monthly_membership_analysis — map cleanly onto ReportTreeTable (label columns,
 * District→Church rows, dollar/value that auto-sums; footer from the `aggregated` block).
 * general_information has two leaves per entry ($ + Total Attendance) so it is rendered
 * as a read-only pivot via buildGeneralInfoPivot (IncomePivotTable).
 */

export type StatsSectionKind = "nation_office" | "membership";

const FIELD = (kpiId: number | undefined, idx: number) => `k${kpiId ?? `i${idx}`}`;

function sectionRows(src: { nation_office_finances?: { rows: any[] }; monthly_membership_analysis?: { rows: StatLeaf[] } }, kind: StatsSectionKind): any[] {
  return kind === "nation_office" ? (src.nation_office_finances?.rows ?? []) : (src.monthly_membership_analysis?.rows ?? []);
}

/** Extract the numeric cell value for a section row by kind. */
function rowValue(row: any, kind: StatsSectionKind): number | null {
  if (kind === "nation_office") {
    const v = row?.dollar?.value;
    return typeof v === "number" ? v : null;
  }
  const n = Number(row?.value);
  return Number.isFinite(n) ? n : null;
}

/** Flat columns derived from the aggregated section's rows (stable across churches). */
export function statsColumnsFromAggregate(agg: FinanceStatisticsAggregate, kind: StatsSectionKind): ReportColumnsSpec {
  const rows = sectionRows(agg, kind);
  const columns = rows.map((r, i) => ({ id: FIELD(r.kpi_id, i), header: r.label, field: FIELD(r.kpi_id, i) }));
  return { nameHeader: "", groups: [{ header: "", columns }] };
}

/** Footer totals { field: value } from the aggregated section. */
export function statsFooterFromAggregate(agg: FinanceStatisticsAggregate, kind: StatsSectionKind): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  sectionRows(agg, kind).forEach((r, i) => { out[FIELD(r.kpi_id, i)] = rowValue(r, kind); });
  return out;
}

/** { field: value } for one church/entity's section rows. */
function entityFieldMap(src: any, kind: StatsSectionKind): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  sectionRows(src, kind).forEach((r, i) => { out[FIELD(r.kpi_id, i)] = rowValue(r, kind); });
  return out;
}

function churchRow(church: FinanceStatisticsChurchEntry, kind: StatsSectionKind): ReportMetricRow {
  return {
    id: `church:${church.church_id}`,
    name: church.church_name,
    church_id: church.church_id,
    church_name: church.church_name,
    department_report_id: church.department_submission?.department_report_id ?? null,
    report_status_name: church.department_submission?.report_status_name ?? null,
    media: church.media ?? [],
    ...entityFieldMap(church, kind),
  };
}

/** District scope: one row per church. */
export function buildStatsDistrictTree(churches: FinanceStatisticsChurchEntry[] | undefined, kind: StatsSectionKind): ReportMetricRow[] {
  return (churches ?? []).map((c) => churchRow(c, kind));
}

/** Nation scope: district parent (API subtotal) → church sub-rows. */
export function buildStatsNationTree(districts: NationFinanceStatisticsDistrictEntry[] | undefined, kind: StatsSectionKind): ReportMetricRow[] {
  return (districts ?? []).map((d) => ({
    id: `district:${d.district_id}`,
    name: d.district_name,
    district_id: d.district_id,
    district_name: d.district_name,
    department_report_id: d.department_submission?.department_report_id ?? null,
    report_status_name: d.department_submission?.report_status_name ?? null,
    ...entityFieldMap(d.aggregated, kind),
    subRows: (d.churches ?? []).map((c) => churchRow(c, kind)),
  }));
}

// ── general_information pivot ($ + Total Attendance) ─────────────────────────

const GI_MEASURES = [
  { key: "dollar", label: "$" },
  { key: "total_attendance", label: "Total Attendance" },
];

type GIEntry = { church_id: number; church_name: string; general_information: Record<string, any> };

function giColumns(agg: FinanceStatisticsAggregate) {
  // Synthetic numeric ids per general_information key, in payload order.
  return Object.entries(agg.general_information ?? {}).map(([key, entry], i) => ({ kpi_id: i, source: (entry as any).label ?? key, key }));
}

function giMeasureRows(entry: Record<string, any>, cols: Array<{ kpi_id: number; key: string }>): PivotMeasureRow[] {
  return GI_MEASURES.map((m) => {
    const values: Record<number, number | null> = {};
    for (const col of cols) {
      const e = entry?.[col.key];
      const raw = m.key === "dollar" ? e?.dollar?.value : e?.total_attendance?.value;
      const n = Number(raw);
      values[col.kpi_id] = Number.isFinite(n) ? n : null;
    }
    return { key: m.key, label: m.label, values };
  });
}

function sumGi(rowSets: PivotMeasureRow[][], cols: Array<{ kpi_id: number }>): PivotMeasureRow[] {
  return GI_MEASURES.map((m, mi) => {
    const values: Record<number, number | null> = {};
    for (const col of cols) {
      let total = 0; let any = false;
      for (const set of rowSets) {
        const v = set[mi]?.values[col.kpi_id];
        if (typeof v === "number") { total += v; any = true; }
      }
      values[col.kpi_id] = any ? total : null;
    }
    return { key: m.key, label: m.label, values };
  });
}

/** Build a PivotModel for general_information (district churches or nation districts→churches). */
export function buildGeneralInfoPivot(
  agg: FinanceStatisticsAggregate,
  scope: "district" | "nation",
  churches: GIEntry[] | undefined,
  districts: Array<{ district_id: number; district_name: string; aggregated: FinanceStatisticsAggregate; churches: GIEntry[] }> | undefined
): PivotModel {
  const cols = giColumns(agg);
  const columns = cols.map((c) => ({ kpi_id: c.kpi_id, source: c.source }));
  const measures = GI_MEASURES;

  if (scope === "district") {
    const entities = (churches ?? []).map((c) => ({
      id: `church:${c.church_id}`,
      name: c.church_name,
      level: "church" as const,
      measureRows: giMeasureRows(c.general_information, cols),
    }));
    return { columns, measures, entities, grandTotal: sumGi(entities.map((e) => e.measureRows), cols) };
  }

  const allSets: PivotMeasureRow[][] = [];
  const entities = (districts ?? []).map((d) => {
    const children = (d.churches ?? []).map((c) => ({
      id: `church:${c.church_id}`,
      name: c.church_name,
      level: "church" as const,
      measureRows: giMeasureRows(c.general_information, cols),
    }));
    children.forEach((ch) => allSets.push(ch.measureRows));
    return {
      id: `district:${d.district_id}`,
      name: d.district_name,
      level: "district" as const,
      measureRows: sumGi(children.map((c) => c.measureRows), cols),
      children,
    };
  });
  return { columns, measures, entities, grandTotal: sumGi(allSets, cols) };
}
